import {
  FinanceCalcGrid,
  OrientationVal,
  TextSize,
} from '@tribal-worldwide-london/ui-component-library-vwg';
import { FuelTypes } from '@tribal-worldwide-london/fa-pdb-service-vwg';
import {
  CTA,
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TextTag,
} from '@volkswagen-onehub/components-core';
import { useState } from 'react';
import { useTribalContext } from '@/context/tribal';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import Box from '@/modules/components/Box/Box';
import SpecificationsOverlay from '@/modules/features/components/SpecificationsOverlay/SpecificationsOverlay';
import { ButtonContainer } from '@/modules/pages/Terms';
import { isMobile } from '@/modules/utils/breakpoints';
import {
  cubicCapacityToLitres,
  formatSpec,
  getEmissionsConsumptionValue,
} from '@/modules/utils/storeUtils';
import useGlobalStore, {
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import { performVWBasicButtonClick } from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import {
  fuelType,
  getDisplayFuelTypeName,
} from '@/modules/constants/fuelType';

function Specifications({
  activeConfiguration,
  fuelTypeName,
}: {
  activeConfiguration: GlobalStoreStateConfiguration;
  fuelTypeName: FuelTypes | string;
}) {
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const isMobileWidth = useResizeResponsive(isMobile);
  const { retailers } = useGlobalStore((state) => state.app);
  const invalidSpecFallback = contentManager.get('spec-missing-feature-label');

  const emissionsCombined = getEmissionsConsumptionValue(
    activeConfiguration,
    fuelTypeName,
    'emissions',
    'combined'
  );

  const consumptionCombined = getEmissionsConsumptionValue(
    activeConfiguration,
    fuelTypeName,
    'consumption',
    'combined'
  );

  const electric = [
    {
      label: contentManager.get('battery-capacity-label'),
      text: formatSpec(
        activeConfiguration.pdbData?.technicalSpecifications?.battery
          ?.capacity ??
          activeConfiguration.stockData?.derivativeExtra?.battery?.capacity,
        invalidSpecFallback,
        ' kWh',
        true
      ),
    },
    {
      label: contentManager.get('energy-consumption-label'),
      text: formatSpec(
        consumptionCombined?.value,
        invalidSpecFallback,
        ` ${consumptionCombined?.unit}`,
        true
      ),
    },
  ];

  const nonElectric = [
    {
      label: contentManager.get('engine-label'),
      text: formatSpec(
        cubicCapacityToLitres(
          activeConfiguration.pdbData?.technicalSpecifications?.engine
            ?.cubicCapacity
        ),
        invalidSpecFallback,
        ' litres'
      ),
    },
    {
      label: contentManager.get('fuel-consumption-label'),
      text: formatSpec(
        consumptionCombined?.value,
        invalidSpecFallback,
        ` ${consumptionCombined?.unit}`,
        true
      ),
    },
  ];

  const specs = [
    {
      label: contentManager.get('fuel-type-label'),
      text: formatSpec(
        getDisplayFuelTypeName(fuelTypeName),
        invalidSpecFallback
      ),
    },
    {
      label: contentManager.get('gearbox-label'),
      text: formatSpec(
        activeConfiguration.pdbData?.technicalSpecifications?.transmission
          .gearboxTypeName,
        invalidSpecFallback
      ),
    },
    {
      label: contentManager.get('emissions-label'),
      text: formatSpec(
        emissionsCombined?.value,
        invalidSpecFallback,
        ` ${emissionsCombined?.unit}`,
        true
      ),
    },
    ...(!(activeConfiguration.vin || activeConfiguration.orderNumber)
      ? [
          {
            label: contentManager.get('acceleration-label'),
            text: formatSpec(
              activeConfiguration.pdbData?.technicalSpecifications?.performance
                .acceleration,
              invalidSpecFallback,
              ' seconds'
            ),
          },
        ]
      : []),
    ...(fuelTypeName === fuelType.ELECTRIC ? electric : nonElectric),
  ];

  const openOverlay = () => {
    setIsOverlayOpen(true);
    performVWBasicButtonClick({
      label: contentManager.get('ctas-tech-specifications'),
      sectionName: SectionNames.SINGLE_PAGE_SPECIFICATIONS,
      configuration: activeConfiguration,
      retailers,
      analyticsManager,
      analyticsBuilder,
    });
  };

  return (
    <>
      <Box
        mb={DesignTokenSizeEnum.static350}
        b960={{ mb: DesignTokenSizeEnum.static515 }}
      >
        <Box mb={DesignTokenSizeEnum.static250}>
          <Text tag={TextTag.h2} appearance={TokenTextAppearance.headline400} bold>
            {contentManager.get('specifications-section-title-normal')}
            {isMobileWidth && <br />}
              {' '}
              {contentManager.get('specifications-section-title-bold')}
          </Text>
        </Box>

        <Text appearance={TokenTextAppearance.bigcopy100}>
          {contentManager.get('specifications-section-subtitle')}
        </Text>
      </Box>

      <Box
        mb={DesignTokenSizeEnum.static350}
        b960={{ mb: DesignTokenSizeEnum.static515 }}
      >
        <FinanceCalcGrid
          financeCalc={specs}
          background={false}
          layoutOrientation={OrientationVal.HORIZONTAL}
          itemOrientation={OrientationVal.VERTICAL}
          textSize={TextSize.LG}
        />
      </Box>
      <ButtonContainer>
        <CTA tag="button" emphasis="primary" onClick={openOverlay}>
          {contentManager.get('ctas-tech-specifications')}
        </CTA>
      </ButtonContainer>
      {isOverlayOpen && (
        <SpecificationsOverlay
          activeConfiguration={activeConfiguration}
          handleClose={() => setIsOverlayOpen(false)}
        />
      )}
    </>
  );
}

export default Specifications;
